import { useContext } from "react";
import { AppContext } from "../../../pages/root/Root";
import PropTypes from "prop-types";

import Grid from "@mui/material/Unstable_Grid2";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material/";

import { Search } from "@mui/icons-material";

import { NumericInput } from "../../common/numericInput/NumericInput";

import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers/";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { initLocalization } from "../../../utils/helpers";
import { creativeAnalysisFiltersStrings } from "./locale";

import {
  MAPPING_KEYS_DATE_FORMAT,
  MAPPING_KEYS_API_DATE_FORMAT,
  MAPPING_KEYS_STATUSES,
  START_DATE,
  END_DATE,
} from "../../../constants";

import "./style.scss";

const InputField = ({ by, onChange, ...props }) => {
  return ["CreativeId", "ProductId", "AdvertiserId"].includes(by) ? (
    <NumericInput
      {...props}
      setValue={(val) => onChange({ target: { name: props.name, value: val } })}
    />
  ) : (
    <TextField {...props} onChange={onChange} />
  );
};

export const CreativeAnalysisFilters = ({ filters, setFilters, onSearch }) => {
  initLocalization(creativeAnalysisFiltersStrings);

  const { language } = useContext(AppContext);

  const handleSetFilters = (event) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [event.target.name]: event.target.value,
      };

      return next;
    });
  };

  const handleFieldKeydown = (event) => {
    if (event.key === "Enter" && event.target.value) {
      event.stopPropagation();
      onSearch();
    }
  };

  const handleDateChange = (value, name) => {
    setFilters({
      ...filters,
      [name]: dayjs(value).format(MAPPING_KEYS_API_DATE_FORMAT),
    });
  };

  const handleSelectStatuses = (event) => {
    const statuses = event.target.value;

    setFilters({
      ...filters,
      statuses,
    });
  };

  return (
    <Grid container className="creative-analysis-filters">
      <Grid
        xs={12}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
      >
        <Box className="dual-field__container">
          <FormControl className="dual-field__select" size="small">
            <InputLabel id="id-label">
              {creativeAnalysisFiltersStrings.id}
            </InputLabel>
            <Select
              labelId="id-label"
              id="id-select"
              value={filters.idSearchField}
              label={creativeAnalysisFiltersStrings.id}
              onChange={handleSetFilters}
              name="idSearchField"
            >
              <MenuItem value="CreativeId">
                {creativeAnalysisFiltersStrings.creativeId}
              </MenuItem>
              <MenuItem value="Asin">
                {creativeAnalysisFiltersStrings.asin}
              </MenuItem>
            </Select>
          </FormControl>
          <InputField
            id="outlined-disabled"
            size="small"
            className="dual-field__input-text"
            value={filters.idKeyword}
            onChange={handleSetFilters}
            onKeyDown={handleFieldKeydown}
            name="idKeyword"
            by={filters.idSearchField}
          />
        </Box>

        <Box className="dual-field__container">
          <FormControl className="dual-field__select" size="small">
            <InputLabel id="taxonomy-label">
              {creativeAnalysisFiltersStrings.taxonomy}
            </InputLabel>
            <Select
              labelId="taxonomy-label"
              id="taxonomy-select"
              value={filters.taxonomySearchField}
              label={creativeAnalysisFiltersStrings.taxonomy}
              onChange={handleSetFilters}
              name="taxonomySearchField"
            >
              <MenuItem value="Product">
                {creativeAnalysisFiltersStrings.product}
              </MenuItem>
              <MenuItem value="Advertiser">
                {creativeAnalysisFiltersStrings.advertiser}
              </MenuItem>
              <MenuItem value="ProductId">
                {creativeAnalysisFiltersStrings.productId}
              </MenuItem>
              <MenuItem value="AdvertiserId">
                {creativeAnalysisFiltersStrings.advertiserId}
              </MenuItem>
            </Select>
          </FormControl>
          <InputField
            id="outlined-disabled"
            size="small"
            className="dual-field__input-text"
            value={filters.taxonomyKeyword}
            onChange={handleSetFilters}
            onKeyDown={handleFieldKeydown}
            name="taxonomyKeyword"
            by={filters.taxonomySearchField}
          />
        </Box>

        <Grid
          xs={12}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            "&": { flex: 1 },
            "& .MuiTextField-root": { width: "100%" },
          }}
        >
          <Box>
            <TextField
              label={creativeAnalysisFiltersStrings.adName}
              size="small"
              name="advertiserName"
              value={filters.advertiserName}
              onChange={handleSetFilters}
              onKeyDown={handleFieldKeydown}
            />
          </Box>

          <Box>
            <TextField
              label={creativeAnalysisFiltersStrings.adPaid}
              size="small"
              name="advertiserPaid"
              value={filters.advertiserPaid}
              onChange={handleSetFilters}
              onKeyDown={handleFieldKeydown}
            />
          </Box>
        </Grid>

        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={language}
        >
          <Grid
            xs={12}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
              "&": { flex: "1" },
              "& .MuiFormLabel-root": { maxWidth: "calc(100% - 2.875rem)" },
              "& .MuiFormLabel-root.MuiFormLabel-filled, & .MuiFormLabel-root.Mui-focused":
                {
                  maxWidth: "calc(133% - 2rem)",
                },
            }}
          >
            <DatePicker
              label={creativeAnalysisFiltersStrings.startDate}
              slotProps={{ textField: { size: "small", readOnly: true } }}
              format={MAPPING_KEYS_DATE_FORMAT}
              value={filters[START_DATE]}
              maxDate={
                filters[END_DATE] ? dayjs(filters[END_DATE]) : dayjs(new Date())
              }
              onChange={(value) => handleDateChange(value, START_DATE)}
            />

            <DatePicker
              label={creativeAnalysisFiltersStrings.endDate}
              slotProps={{ textField: { size: "small", readOnly: true } }}
              format={MAPPING_KEYS_DATE_FORMAT}
              value={filters[END_DATE]}
              minDate={filters[START_DATE] ? dayjs(filters[START_DATE]) : null}
              maxDate={dayjs(new Date())}
              onChange={(value) => handleDateChange(value, END_DATE)}
            />
          </Grid>
        </LocalizationProvider>

        <Box
          sx={{
            "&": { flex: 1 },
            "& .MuiTextField-root": { width: "100%" },
          }}
        >
          <TextField
            label={creativeAnalysisFiltersStrings.keyword}
            size="small"
            name="keyword"
            value={filters.keyword}
            onChange={handleSetFilters}
            onKeyDown={handleFieldKeydown}
          />
        </Box>

        <Box
          sx={{
            "&": {
              flex: "1",
            },
            "& .MuiTextField-root": {
              width: "100%",
            },
          }}
        >
          <FormControl sx={{ width: "100%" }} size="small">
            <InputLabel id="sponsor-label">
              {creativeAnalysisFiltersStrings.sponsorType}
            </InputLabel>
            <Select
              labelId="sponsor-label"
              id="sponsor-select"
              value={filters.sponsorType}
              label={creativeAnalysisFiltersStrings.sponsorType}
              onChange={handleSetFilters}
              name="sponsorType"
            >
              <MenuItem value="All">
                {creativeAnalysisFiltersStrings.all}
              </MenuItem>
              <MenuItem value="Product">
                {creativeAnalysisFiltersStrings.product}
              </MenuItem>
              <MenuItem value="Brand">
                {creativeAnalysisFiltersStrings.brand}
              </MenuItem>
              <MenuItem value="Boutique">
                {creativeAnalysisFiltersStrings.boutique}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            "&": {
              flex: "1",
              maxWidth: "calc(100% / 8)",
            },
            "& .MuiTextField-root": {
              width: "100%",
            },
          }}
        >
          <FormControl sx={{ width: "100%" }} size="small">
            <InputLabel id="status-label">
              {creativeAnalysisFiltersStrings.status}
            </InputLabel>
            <Select
              labelId="status-label"
              id="status-select"
              multiple
              value={filters.statuses}
              label={creativeAnalysisFiltersStrings.status}
              onChange={handleSelectStatuses}
              input={
                <OutlinedInput label={creativeAnalysisFiltersStrings.status} />
              }
              renderValue={(selected) => selected.join(", ")}
            >
              <MenuItem value={MAPPING_KEYS_STATUSES.validated}>
                <Checkbox
                  checked={filters.statuses.includes(
                    MAPPING_KEYS_STATUSES.validated,
                  )}
                />
                <ListItemText
                  primary={creativeAnalysisFiltersStrings.validated}
                />
              </MenuItem>
              <MenuItem value={MAPPING_KEYS_STATUSES.waitingProductCreated}>
                <Checkbox
                  checked={filters.statuses.includes(
                    MAPPING_KEYS_STATUSES.waitingProductCreated,
                  )}
                />
                <ListItemText
                  primary={creativeAnalysisFiltersStrings.productCreated}
                />
              </MenuItem>
              <MenuItem value={MAPPING_KEYS_STATUSES.waitingProductRequested}>
                <Checkbox
                  checked={filters.statuses.includes(
                    MAPPING_KEYS_STATUSES.waitingProductRequested,
                  )}
                />
                <ListItemText
                  primary={creativeAnalysisFiltersStrings.productRequested}
                />
              </MenuItem>
              <MenuItem value={MAPPING_KEYS_STATUSES.rejected}>
                <Checkbox
                  checked={filters.statuses.includes(
                    MAPPING_KEYS_STATUSES.rejected,
                  )}
                />
                <ListItemText
                  primary={creativeAnalysisFiltersStrings.rejected}
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <span>
          <Button
            size="large"
            startIcon={<Search />}
            variant="contained"
            color="secondary"
            onClick={onSearch}
          >
            {creativeAnalysisFiltersStrings.search}
          </Button>
        </span>
      </Grid>
    </Grid>
  );
};

CreativeAnalysisFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};
